import { useState } from "react";
import { Tooltip } from "antd";
import { SolutionOutlined, MailOutlined } from "@ant-design/icons";
import { fetchClients } from "../../../services/customer360Service";
import EmailLogsDrawer from "./EmailLogsDrawer";
import {
  cardStyle, dateTime, relative, chip, TONE,
  PanelHeader, PanelTable, PanelSkeleton, PanelError, usePanelData,
  SearchBox,
} from "./shared";

/**
 * The customer's own clients — the proprietors they file on behalf of.
 *
 * An attorney firm's account is really a list of these, so the row carries both
 * identities: who the client is, and which attorney number they sit under.
 */
const ClientsTab = ({ userId, active }) => {
  const p = usePanelData(fetchClients, userId, active);
  const [mailingTo, setMailingTo] = useState(null);

  if (p.loading && !p.data) return <PanelSkeleton />;
  if (p.error) return <PanelError error={p.error} onRetry={p.reload} />;

  const columns = [
    {
      title: "Client", key: "client", width: 220,
      /* Both names in one cell. They are usually the same, and two columns
         repeating each other costs width the identifiers need. */
      render: (_, r) => {
        const primary = r.name || r.client_name;
        const secondary = r.client_name && r.client_name !== r.name ? r.client_name : null;
        return (
          <div>
            <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 12.5 }}>{primary || "—"}</div>
            {secondary && <div style={{ fontSize: 11.5, color: "#94a3b8" }}>{secondary}</div>}
          </div>
        );
      },
    },
    {
      title: "Attorney", key: "attorney", width: 185,
      render: (_, r) => (r.attorney_number
        ? (
          <div>
            <div style={{ fontSize: 12.5, color: "#334155", fontWeight: 600 }}>{r.attorney_number}</div>
            {r.attorney_name && <div style={{ fontSize: 11.5, color: "#94a3b8" }}>{r.attorney_name}</div>}
          </div>
        )
        : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Email", dataIndex: "email", width: 210,
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", wordBreak: "break-all" }}>{v || "—"}</span>,
    },
    {
      title: "Mobile", dataIndex: "mobile", width: 130,
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", fontVariantNumeric: "tabular-nums" }}>{v || "—"}</span>,
    },
    {
      title: "GSTIN", dataIndex: "gstin", width: 165,
      render: (v) => <span style={{ fontSize: 12, color: "#64748b", fontFamily: "ui-monospace,SFMono-Regular,monospace" }}>{v || "—"}</span>,
    },
    {
      title: "PAN", dataIndex: "pan", width: 125,
      render: (v) => <span style={{ fontSize: 12, color: "#64748b", fontFamily: "ui-monospace,SFMono-Regular,monospace" }}>{v || "—"}</span>,
    },
    {
      title: "Status", dataIndex: "status", width: 105,
      /* The column arrives as a flag on some rows and a word on others, so both
         shapes are read rather than one being assumed. */
      render: (v) => {
        const on = v === 1 || v === "1" || String(v).toLowerCase() === "active";
        return chip(on ? "Active" : "Inactive", on ? TONE.green : TONE.slate);
      },
    },
    {
      title: "Last email", dataIndex: "last_email_sent_at", width: 145,
      render: (v) => (v
        ? <Tooltip title={dateTime(v)}><span style={{ fontSize: 12.5, color: "#475569", whiteSpace: "nowrap" }}>{relative(v)}</span></Tooltip>
        : <span style={{ color: "#cbd5e1", fontSize: 12 }}>Never</span>),
    },
    {
      title: "Added", dataIndex: "created_at", width: 165,
      render: (v) => <span style={{ fontSize: 12.5, color: "#94a3b8", whiteSpace: "nowrap" }}>{dateTime(v)}</span>,
    },
    {
      title: "Emails", key: "emails", width: 110, fixed: "right",
      render: (_, r) => (
        <button
          onClick={() => setMailingTo(r)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px",
            borderRadius: 8, border: "1px solid #bfdbfe", background: "#eff6ff",
            color: "#2563eb", fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}
        >
          <MailOutlined /> View
        </button>
      ),
    },
  ];

  return (
    <div style={cardStyle}>
      <PanelHeader
        icon={<SolutionOutlined />}
        title="Clients"
        subtitle="The proprietors this account files on behalf of"
        iconBg="linear-gradient(135deg,#0891b2,#06b6d4)"
        right={<SearchBox value={p.search} onChange={p.setSearch} placeholder="Search name, email, attorney no…" width={250} />}
      />
      <PanelTable
        columns={columns}
        rows={p.rows}
        loading={p.loading}
        page={p.page}
        total={p.total}
        perPage={p.perPage}
        onPage={p.goToPage}
        empty={p.searching ? "No matches for that search." : "No clients added on this account yet."}
        scrollX={1560}
      />
      <EmailLogsDrawer
        userId={userId}
        recipient={mailingTo}
        recipientType="proprietor"
        onClose={() => setMailingTo(null)}
      />
    </div>
  );
};

export default ClientsTab;
